import { addressToText } from '@dadohr/shared/domainTypes/address';
import { Doc } from '@dadohr/shared/domainTypes/document';
import { IOrganization } from '@dadohr/shared/domainTypes/org';
import { IUser } from '@dadohr/shared/domainTypes/user';
import { FS } from '@dadohr/shared/versions';
import styled from '@emotion/styled';
import { Button } from '@material-ui/core';
import { compact, groupBy, keyBy } from 'lodash';
import moment from 'moment-timezone';
import React, { useEffect, useMemo, useState } from 'react';
import shortid from 'shortid';
import { ExportButton } from '../../../../../../../components/Exporter';
import { IExportColumn } from '../../../../../../../components/Exporter/service';
import { RowsRendererScrollContainer } from '../../../../../../../components/GroupableList';
import { PaperLoader } from '../../../../../../../components/Loader';
import { TableSearch } from '../../../../../../../components/Table';
import { UserTable } from '../../../../../../../components/UserTable';
import { useUserTableColumnsAndSorters } from '../../../../../../../components/UserTable/columns';
import { useDialogState } from '../../../../../../../hooks/useDialogState';
import { CanvasBar } from '../../../../../../../layout/Canvas';
import { Section } from '../../../../../../../layout/Section';
import {
  removeDoc,
  setDoc,
  updateDoc
} from '../../../../../../../services/firebase';
import { toMoment } from '../../../../../../../services/time';
import {
  EMPTY_USER,
  matchUserBySearchTerm,
  toSearchTermRegexp,
  useUsersByOrgId
} from '../../../../../../../services/user';
import { UserDetailsDialog } from './DetailsDialog';

type Props = {
  org: Doc<IOrganization>;
};

const HEIGHT = 600;

type D = Doc<IUser>;
type ColumnName =
  | 'id'
  | 'managerEmail'
  | 'managerAlternateId'
  | keyof IUser
  | string; // TODO manager email
type ExportColumn = IExportColumn<
  ColumnName,
  D,
  { usersById: { [key: string]: Doc<IUser> } }
>;

// TODO translate
const EXPORT_COLUMNS: ExportColumn[] = [
  {
    key: 'id',
    head: () => 'ID',
    cell: (d) => d.id
  },
  {
    key: 'alternateId',
    head: () => 'Alternate ID',
    cell: (d) => d.data.alternateId
  },
  {
    key: 'email',
    head: () => 'Work Email',
    cell: (d) => d.data.email
  },
  {
    key: 'personalEmail',
    head: () => 'Personal Email',
    cell: (d) => d.data.personalEmail
  },
  {
    key: 'firstName',
    head: () => 'First Name',
    cell: (d) => d.data.firstName
  },
  {
    key: 'middleName',
    head: () => 'Middle Name',
    cell: (d) => d.data.middleName
  },
  {
    key: 'preferredName',
    head: () => 'Preferred Name',
    cell: (d) => d.data.preferredName
  },
  {
    key: 'lastName',
    head: () => 'Last Name',
    cell: (d) => d.data.lastName
  },
  {
    key: 'gender',
    head: () => 'Gender',
    cell: (d) => d.data.gender
  },
  {
    key: 'orgId',
    head: () => 'Organization ID',
    cell: (d) => d.data.orgId
  },
  {
    key: 'managerId',
    head: () => 'Manager ID',
    cell: (d) => d.data.managerId
  },
  {
    key: 'managerAlternateId',
    head: () => 'Manager Alternate ID',
    cell: (d, o) => o.usersById[d.data.managerId]?.data.alternateId || ''
  },
  {
    key: 'managerEmail',
    head: () => 'Manager Email',
    cell: (d, o) => o.usersById[d.data.managerId]?.data.email || ''
  },
  {
    key: 'location',
    head: () => 'Location',
    cell: (d) => d.data.location
  },
  {
    key: 'role',
    head: () => 'Role',
    cell: (d) => d.data.role
  },
  {
    key: 'department',
    head: () => 'Department',
    cell: (d) => d.data.department
  },
  {
    key: 'team',
    head: () => 'Team',
    cell: (d) => d.data.team
  },
  {
    key: 'hireDate',
    head: () => 'First day at work',
    cell: (d) =>
      d.data.hireDate ? toMoment(d.data.hireDate).toISOString() : ''
  },
  {
    key: 'terminationDate',
    head: () => 'Termination Date',
    cell: (d) =>
      d.data.terminationDate
        ? toMoment(d.data.terminationDate).toISOString()
        : ''
  },
  {
    key: 'address',
    head: () => 'Address',
    cell: (d) => addressToText(d.data.address)
  },
  {
    key: 'phoneNumber',
    head: () => 'Phone Number',
    cell: (d) => d.data.phoneNumber
  },
  {
    key: 'dateOfBirth',
    head: () => 'Date of birth',
    cell: (d) =>
      d.data.dateOfBirth ? toMoment(d.data.dateOfBirth).toISOString() : ''
  },
  {
    key: 'active',
    head: () => 'Active',
    cell: (d) => (d.data.active ? 'true' : 'false')
  }
];

const PRESELECTED_COLUMNS: ColumnName[] = [
  // 'alternateId',
  'email',
  'personalEmail',
  'firstName',
  'middleName',
  'preferredName',
  'lastName',
  'gender',
  'managerAlternateId',
  'managerEmail',
  'location',
  'role',
  'department',
  'team',
  'hireDate',
  'terminationDate'
];

const TableActions = styled('div')((p) => ({
  display: 'flex',
  justifyContent: 'flex-end',
  alignItems: 'center',
  gap: p.theme.spacing()
}));

const userWithTrimmedAndLowerCasedEmails = (user: IUser): IUser => {
  return {
    ...user,
    email: (user.email || '').trim().toLowerCase(),
    personalEmail: (user.personalEmail || '').trim().toLowerCase()
  };
};

export const Users: React.FC<Props> = ({ org }) => {
  const [users, loading] = useUsersByOrgId(org.id);
  const usersById = useMemo(() => keyBy(users || [], (u) => u.id), [users]);
  const otherProps = useMemo(() => ({ usersById }), [usersById]);
  const [editDialogUser, setEditDialogUser] = useState<Doc<IUser> | null>(null);
  const addDialog = useDialogState();
  const [q, setQ] = useState('');
  const { columns, sorters } = useUserTableColumnsAndSorters(
    org.data.customUserFields
  );

  useEffect(() => {
    if (!users) {
      return;
    }
    const byEmail = groupBy(users, (u) => u.data.email);
    const duplicates = compact(
      Object.entries(byEmail).map(([email, ds]) => {
        if (ds.length > 1) {
          return { email, users: ds };
        }
        return null;
      })
    );
    if (duplicates.length) {
      console.log('Duplicate email addresses found', duplicates);
    }

    console.log('USERS', users);
  }, [users]);

  const filteredUsers = useMemo(() => {
    if (!users || !q) {
      return users;
    }
    const regexp = toSearchTermRegexp(q);
    return users.filter((u) => matchUserBySearchTerm(regexp, u));
  }, [users, q]);

  const { customUserFields } = org.data;
  const exportColumns: ExportColumn[] = useMemo(() => {
    return [
      ...EXPORT_COLUMNS,
      ...customUserFields.map((c) => {
        return {
          key: `customField.${c.key}`,
          head: () => c.label,
          cell: (d: D, o: { usersById: { [key: string]: Doc<IUser> } }) => {
            const val: any = d.data.customFields[c.key];
            if (val === undefined || val === null) {
              return '';
            }
            if (c.type === 'address') {
              return addressToText(val);
            }
            if (c.type === 'date' || c.type === 'smartDate') {
              return toMoment(val).toISOString();
            }
            if (c.type === 'relationship') {
              return o.usersById[val]?.data.email || '';
            }
            if (c.type === 'boolean') {
              return val ? 'true' : 'false';
            }
            return `${val}`;
          }
        };
      })
    ];
  }, [customUserFields]);

  return (
    <Section>
      <CanvasBar>
        <div>Users {users && `(${users.length})`}</div>
        <TableActions>
          <TableSearch term={q} onChange={setQ} />
          <ExportButton
            dialogTitle="Export users"
            fileName={`${org.data.name} - Users - ${moment().format(
              'YYYY-MM-DD'
            )}`}
            variant="contained"
            color="primary"
            rows={users || []}
            disabled={loading}
            columns={exportColumns}
            otherProps={otherProps}
            size="small"
            preselectedColumns={PRESELECTED_COLUMNS}
          >
            Export...
          </ExportButton>

          <Button
            size="small"
            variant="contained"
            color="secondary"
            onClick={addDialog.openDialog}
          >
            Add...
          </Button>
        </TableActions>
      </CanvasBar>
      {(loading || !users) && <PaperLoader height={HEIGHT} />}
      {!!filteredUsers && (
        <RowsRendererScrollContainer height={HEIGHT}>
          <UserTable
            ds={filteredUsers}
            columns={columns}
            sorters={sorters}
            onRowClick={(u) => setEditDialogUser(u)}
            dateFormat={org.data.dateFormats.MMDDYYYY}
            headProps={{ sticky: true, offset: 0 }}
            horizontalScroll
          />
        </RowsRendererScrollContainer>
      )}
      {editDialogUser && (
        <UserDetailsDialog
          open={true}
          user={editDialogUser.data}
          userId={editDialogUser.id}
          onClose={() => setEditDialogUser(null)}
          onSubmit={(nextData) =>
            updateDoc(
              {
                ...editDialogUser,
                data: userWithTrimmedAndLowerCasedEmails(nextData)
              },
              (t) => t
            )
          }
          onRemove={async () => {
            if (editDialogUser) {
              removeDoc(editDialogUser);
            }
          }}
          customFields={org.data.customUserFields}
        />
      )}
      {addDialog.dialogOpen && (
        <UserDetailsDialog
          open={true}
          user={EMPTY_USER(org.id)}
          onClose={addDialog.closeDialog}
          onSubmit={(nextData) =>
            setDoc({
              id: shortid(),
              collection: FS.users,
              data: userWithTrimmedAndLowerCasedEmails(nextData)
            })
          }
          customFields={org.data.customUserFields}
        />
      )}
    </Section>
  );
};
